import React, { useState, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";

import { getNextAssetId } from "../utils/assetIdUtils";
import { reverseGeocode } from "../utils/geocodeUtils";

import hydrantIcon from "../markerIcons/hydrant.svg";
import manholeIcon from "../markerIcons/manhole.svg";
import valveIcon from "../markerIcons/valve.svg";
import trafficSignIcon from "../markerIcons/trafficSign.svg";
import waterMeterIcon from "../markerIcons/watermeter.svg";

/**
 * Point asset types available for Quick Capture.
 * Line assets (WaterLine, SewerLine) are excluded since they require polyline drawing.
 */
const ASSET_TYPES = [
    { key: "Hydrant", label: "Hydrant", icon: hydrantIcon },
    { key: "Manhole", label: "Manhole", icon: manholeIcon },
    { key: "Valve", label: "Valve", icon: valveIcon },
    { key: "TrafficSign", label: "Signs", icon: trafficSignIcon },
    { key: "WaterMeter", label: "Water Meter", icon: waterMeterIcon },
];

/**
 * QuickCapture — Photo-First / Quick Capture Workflow
 *
 * Floating camera FAB over the map. Tap to snap a photo, auto-locate via GPS,
 * pick asset type, and save a minimal asset in under 10 seconds.
 *
 * Steps: idle → captured → saving → done (snackbar) → idle
 */
const QuickCapture = forwardRef(function QuickCapture({ serviceData, user, refreshData, onShowError, canWrite }, ref) {
    const [step, setStep] = useState("idle"); // idle | captured | saving | done
    const [photoFile, setPhotoFile] = useState(null);
    const [gpsCoords, setGpsCoords] = useState(null);
    const [gpsError, setGpsError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const fileInputRef = useRef(null);

    /**
     * Step 1: User taps FAB → trigger hidden file input (camera on mobile)
     */
    const handleFabClick = () => {
        if (fileInputRef.current && step === "idle" && canWrite) {
            fileInputRef.current.value = ""; // reset so same file can be re-selected
            fileInputRef.current.click();
        }
    };

    // Expose handleFabClick to parent via ref (must be called before any conditional returns)
    useImperativeHandle(ref, () => ({
        triggerCapture: handleFabClick
    }));

    // Don't render anything if user doesn't have write permission
    if (!canWrite) return null;

    /**
     * Step 2: Photo captured → get GPS position → show asset picker
     */
    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        setPhotoFile(file);
        setGpsError(null);

        // Get GPS location
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    setGpsCoords({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                    });
                    setStep("captured");
                },
                (err) => {
                    console.warn("[QuickCapture] GPS error:", err.message);
                    setGpsError("Could not get your location. Please ensure location services are enabled.");
                    setStep("captured");
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        } else {
            setGpsError("Location services are not supported by this browser.");
            setStep("captured");
        }
    };

    /**
     * Step 3: User picks asset type → auto-fill ID + geocode → save
     */
    const handleAssetTypePick = async (assetType) => {
        if (!gpsCoords) {
            // Can't save without coordinates
            if (onShowError) onShowError("Location not available. Cannot save asset without coordinates.");
            handleCancel();
            return;
        }

        setStep("saving");

        try {
            // 1. Fetch field definitions for this asset type
            const { data: fieldRes } = await axios.get(`/api/assetFields/${assetType}`);
            const assetFields = fieldRes.asset;
            if (!assetFields) {
                throw new Error("Could not load asset field definitions.");
            }
            const inputObjectArray = assetFields.inputObjectArray;

            // 2. Auto-generate Client ID
            const nextId = getNextAssetId(serviceData, assetType);

            // 3. Reverse geocode for address fields
            let geocodedFields = {};
            try {
                const geo = await reverseGeocode(gpsCoords.lat, gpsCoords.lng);
                if (geo) {
                    // Only include fields that exist in the asset's inputObjectArray
                    for (const [fieldName, value] of Object.entries(geo)) {
                        if (inputObjectArray.find((f) => f.name === fieldName && f.display)) {
                            geocodedFields[fieldName] = value;
                        }
                    }
                }
            } catch (geoErr) {
                console.warn("[QuickCapture] Geocode failed, continuing without address:", geoErr);
            }

            // 4. Build the asset record with minimal fields
            const currentUser = user.Created_User || user.email || "Unknown User";
            const currentDate = new Date();

            const { data: guidRes } = await axios.get("/api/newGUID");
            const GlobalId = Object.values(guidRes.result.flat()[0])[0];

            const assetValues = {
                ...geocodedFields,
                Latitude: gpsCoords.lat,
                Longitude: gpsCoords.lng,
                Created_User: currentUser,
                Created_Date: currentDate,
                Last_Editor: currentUser,
                Last_Edited_Date: currentDate,
                ClientID: user.ClientID,
                GlobalID: GlobalId,
                DeleteFacility: "No",
            };

            // Set the auto-incremented Client ID
            if (nextId && nextId.suggestedId) {
                assetValues[nextId.fieldName] = nextId.suggestedId;
            }

            const inputObject = {
                asset: assetType,
                values: assetValues,
                types: inputObjectArray,
            };

            // 5. Insert the asset
            const insertRes = await axios.post("/api/assetInsert", inputObject);
            const assetId = insertRes.data.result.recordset[0][`${assetType}ID`];

            // 6. Upload the photo as an attachment
            if (photoFile && assetId) {
                const formData = new FormData();
                formData.append("file0", photoFile);
                formData.append(
                    "fileData",
                    JSON.stringify({
                        file0: {
                            fileName: photoFile.name || "quick_capture.jpg",
                            fileDescription: "Quick Capture photo",
                        },
                    })
                );
                await axios.post(`/api/uploadAttachment/${assetType}/${assetId}`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            // 7. Success — refresh map data and show snackbar
            setStep("done");
            setSnackbarOpen(true);
            await refreshData();
        } catch (err) {
            console.error("[QuickCapture] Save failed:", err);
            if (onShowError) {
                onShowError("Quick Capture failed: " + (err.response?.data?.error || err.message));
            }
            handleCancel();
        }
    };

    /**
     * Cancel / reset back to idle
     */
    const handleCancel = () => {
        setStep("idle");
        setPhotoFile(null);
        setGpsCoords(null);
        setGpsError(null);
    };

    /**
     * Snackbar close → reset to idle
     */
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setStep("idle");
        setPhotoFile(null);
        setGpsCoords(null);
        setGpsError(null);
    };

    return (
        <>
            {/* Hidden file input for camera/photo capture */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />

            {/* Camera FAB is now rendered in CustomMap.jsx on the left sidebar */}

            {/* Asset type picker overlay */}
            {step === "captured" && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        zIndex: 3000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    onClick={handleCancel}
                >
                    <div
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: "12px",
                            padding: "24px",
                            maxWidth: "340px",
                            width: "90%",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "16px",
                        }}>
                            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600 }}>
                                Select Asset Type
                            </h3>
                            <IconButton size="small" onClick={handleCancel}>
                                <CloseIcon />
                            </IconButton>
                        </div>

                        {/* GPS error warning */}
                        {gpsError && (
                            <div style={{
                                padding: "10px 12px",
                                marginBottom: "12px",
                                backgroundColor: "#fff3e0",
                                borderRadius: "6px",
                                color: "#e65100",
                                fontSize: "13px",
                                textAlign: "center",
                            }}>
                                {gpsError}
                            </div>
                        )}

                        {/* Asset type buttons — 2-column grid */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "10px",
                        }}>
                            {ASSET_TYPES.map((at) => (
                                <Button
                                    key={at.key}
                                    variant="contained"
                                    disabled={!gpsCoords}
                                    onClick={() => handleAssetTypePick(at.key)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "14px 8px",
                                        minHeight: "72px",
                                        textTransform: "none",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                    }}
                                >
                                    <img
                                        src={at.icon}
                                        alt={at.label}
                                        style={{
                                            width: "28px",
                                            height: "28px",
                                            marginBottom: "6px",
                                            filter: "brightness(0) invert(1)",
                                        }}
                                    />
                                    {at.label}
                                </Button>
                            ))}
                        </div>

                        {/* Cancel button */}
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleCancel}
                            sx={{ marginTop: "14px", textTransform: "none" }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {/* Saving overlay with spinner */}
            {step === "saving" && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        zIndex: 3000,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CircularProgress sx={{ color: "#fff", marginBottom: "16px" }} />
                    <span style={{ color: "#fff", fontSize: "16px", fontWeight: 500 }}>
                        Saving asset...
                    </span>
                </div>
            )}

            {/* Success snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2500}
                onClose={handleSnackbarClose}
                message="Asset saved!"
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </>
    );
});

export default QuickCapture;
